import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  Stack,
  TextField,
} from "@origin-digital/ods-core";
// import { Autocomplete } from "@origin-digital/ods-lab";
import { apiClient } from "src/api/apiService";
import { Companies, Company } from "./types";
import { Autocomplete } from "@origin-digital/ods-lab";
import { useDebounce } from "react-use";
import { useFeedContext } from "src/context/Feed";

interface INewProjectProps {
  closeDialog: () => void;
}

export const NewProject: React.FC<INewProjectProps> = ({ closeDialog }) => {
  const { doReload } = useFeedContext();
  const [inputValue, setInputValue] = React.useState<string>("");
  const [companies, setCompanies] = useState<
    {
      label: string;
      value: string;
    }[]
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");
  const [debouncedInputValue, setDebouncedInputValue] = useState<string>("");
  const [projectName, setProjectName] = useState<string | null>(null);
  const onAddProject = async () => {
    try {
      setLoading(true);
      await apiClient.post("/project", {
        name: projectName,
        // eslint-disable-next-line
        requesting_company_id: selectedCompanyId,
      });
      setLoading(false);
      closeDialog();
      await doReload();
    } catch (err) {
      console.error("Failed to add project:", err);
      setLoading(false);
    }
  };

  useDebounce(
    () => {
      setDebouncedInputValue(inputValue);
    },
    300,
    [inputValue]
  );

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get<Companies>("/company/companies", {
          params: debouncedInputValue
            ? {
                name: debouncedInputValue,
              }
            : {},
        });
        setCompanies(
          response.data.companies.map((company: Company) => ({
            label: company.name,
            value: company.id,
          }))
        );
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch companies:", err);
        setLoading(false);
      }
    };

    fetchCompanies();
  }, [debouncedInputValue]);

  return (
    <Dialog
      id="new-project-dialog"
      open={true}
      title="Add new project"
      onClose={closeDialog}
    >
      <Stack space="small">
        {/* Project fields */}
        {/* Box hack to fix issue with TextField label being cropped by Dialog title */}
        <Box />
        {/* End hack */}
        <Stack space="xlarge">
          <TextField
            variant="outlined"
            label="Project name"
            hideHelperTextSpace
            id="requesting-company-label"
            value={projectName || ""}
            onChange={(event) => setProjectName(event.target.value)}
          />
          <Autocomplete
            id="requesting-company"
            textFieldProps={{
              variant: "outlined",
            }}
            label="Requesting company"
            helperText="This will apply to all premises in this project."
            // hideHelperTextSpace
            options={companies}
            value={
              companies.find(
                (company) => company.value === selectedCompanyId
              ) || null
            }
            onChange={(_event, item, reason) => {
              if (reason === "clear") {
                setSelectedCompanyId("");
              } else {
                setSelectedCompanyId(item!.value);
              }
            }}
            inputValue={inputValue}
            onInputChange={(_event, tempValue, reason) => {
              if (reason === "clear") {
                return setInputValue("");
              }
              setInputValue(tempValue);
            }}
            loading={loading}
          />
        </Stack>
        {/* End Project fields */}

        {/* Actions */}
        <Box
          position={{
            lg: "static",
            xs: "fixed",
          }}
          paddingTop={{
            lg: "medium",
            xs: "medium",
          }}
          paddingBottom={{
            lg: "none",
            xs: "large",
          }}
          paddingX={{
            lg: "none",
            xs: "medium",
          }}
          backgroundColor="white"
          width="full"
          style={{
            left: "0",
            bottom: "0",
            zIndex: "1",
          }}
        >
          <Box
            display="flex"
            flexWrap={{
              lg: "nowrap",
              xs: "wrap",
            }}
            justifyContent="flex-end"
            alignItems="center"
          >
            <Box marginRight="large">
              <Button
                variant="text"
                color="primary"
                size="small"
                onClick={closeDialog}
              >
                Cancel
              </Button>
            </Box>
            <Button
              size="small"
              onClick={onAddProject}
              disabled={loading || !projectName || !selectedCompanyId}
            >
              Create project
            </Button>
          </Box>
        </Box>
        {/* End Actions */}
      </Stack>
    </Dialog>
  );
};
